import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Save, X, User, DollarSign, Calendar, CreditCard, FileText, 
  AlertCircle, Wifi, ArrowLeft, Tag, ChevronRight, ChevronLeft,
  Check, Plus, Trash2, Package, Router
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const schema = yup.object().shape({
  clienteId: yup.number().required('Cliente é obrigatório'),
  enderecoId: yup.number().required('Endereço é obrigatório'),
  planoId: yup.number().required('Plano é obrigatório'),
  diaVencimento: yup.number().min(1).max(31).required('Dia de vencimento é obrigatório')
});

const STEPS = [
  { id: 1, name: 'Cliente', icon: User, description: 'Dados do cliente' },
  { id: 2, name: 'Plano', icon: Wifi, description: 'Plano contratado' },
  { id: 3, name: 'Serviços', icon: Package, description: 'Serviços adicionais' },
  { id: 4, name: 'Pagamento', icon: CreditCard, description: 'Dados de pagamento' },
  { id: 5, name: 'Conexão', icon: Router, description: 'Dados de conexão PPPoE' },
  { id: 6, name: 'Resumo', icon: FileText, description: 'Revisar e finalizar' }
];

const ContratoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [servicosAdicionais, setServicosAdicionais] = useState([]);
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [enderecos, setEnderecos] = useState([]);
  const [loadingEnderecos, setLoadingEnderecos] = useState(false);
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  
  const [showEquipamentoModal, setShowEquipamentoModal] = useState(false);
  const [contratoIdCriado, setContratoIdCriado] = useState(null);
  const [equipamentosDisponiveis, setEquipamentosDisponiveis] = useState([]);
  const [equipamentosVinculados, setEquipamentosVinculados] = useState([]);
  const [equipamentoData, setEquipamentoData] = useState({
    equipamentoId: '',
    tipoVinculo: 'comodato',
    valorMensal: 0,
    observacoes: ''
  });
  const [loadingEquipamento, setLoadingEquipamento] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      formaPagamento: 'pix',
      diaVencimento: 10,
      desconto: 0,
      login_pppoe: '',
      senha_pppoe: ''
    }
  });

  const clienteId = watch('clienteId');
  const planoId = watch('planoId');
  const enderecoId = watch('enderecoId');

  useEffect(() => {
    fetchInitialData();
    if (isEdit) {
      fetchContrato();
    }
  }, [id]);

  useEffect(() => {
    if (clienteId) {
      fetchEnderecos(clienteId);
    } else {
      setEnderecos([]);
      setValue('enderecoId', '');
    }
  }, [clienteId]);

  useEffect(() => {
    if (planoId) {
      const plano = planos.find(p => p.id == planoId);
      setPlanoSelecionado(plano);
    }
  }, [planoId, planos]);

  const fetchInitialData = async () => {
    try {
      const [clientesRes, planosRes, servicosRes, equipamentosRes] = await Promise.all([
        api.get('/clientes'),
        api.get('/planos'),
        api.get('/servicos-adicionais/ativos'),
        api.get('/equipamentos/disponiveis')
      ]);
      
      setClientes(clientesRes.data.data);
      setPlanos(planosRes.data.data);
      setServicosAdicionais(servicosRes.data);
      setEquipamentosDisponiveis(equipamentosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados iniciais');
    }
  };

  const fetchEnderecos = async (clienteId) => {
    try {
      setLoadingEnderecos(true);
      const response = await api.get(`/clientes/${clienteId}/enderecos/ids`);
      setEnderecos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar endereços:', error);
      toast.error('Erro ao carregar endereços do cliente');
      setEnderecos([]);
    } finally {
      setLoadingEnderecos(false);
    }
  };

  const fetchContrato = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/contratos/${id}`);
      const contrato = response.data;
      
      // mapear campos retornados para o formulário
      const formValues = {
        ...contrato,
        formaPagamento: contrato.formaPagamento || 'pix',
        login_pppoe: contrato.login || contrato.login_pppoe || '',
        senha_pppoe: contrato.senha || contrato.senha_pppoe || ''
      };

      reset(formValues);
      setPlanoSelecionado(contrato.plano);
      
      if (contrato.clienteId) {
        await fetchEnderecos(contrato.clienteId);
      }
      
      if (contrato.servicosAdicionais) {
        setServicosSelecionados(contrato.servicosAdicionais);
      }
    } catch (error) {
      toast.error('Erro ao carregar contrato');
      navigate('/contratos');
    } finally {
      setLoading(false);
    }
  };

  const calcularValorTotal = () => {
    if (!planoSelecionado) return 0;
    const valorPlano = parseFloat(planoSelecionado.valor) || 0;
    const valorServicos = servicosSelecionados.reduce(
      (total, s) => total + parseFloat(s.valorMensal || 0),
      0
    );
    return valorPlano + valorServicos;
  };

  const adicionarServicoAdicional = (servico) => {
    const jaAdicionado = servicosSelecionados.find(s => s.id === servico.id);
    if (jaAdicionado) {
      toast.error('Serviço já adicionado');
      return;
    }
    
    setServicosSelecionados([...servicosSelecionados, servico]);
    toast.success(`${servico.nome} adicionado`);
  };

  const removerServicoAdicional = (servicoId) => {
    setServicosSelecionados(servicosSelecionados.filter(s => s.id !== servicoId));
    toast.success('Serviço removido');
  };

  const nextStep = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    let isValid = true;
    
    if (currentStep === 1) {
      isValid = await trigger(['clienteId', 'enderecoId']);
    } else if (currentStep === 2) {
      isValid = await trigger(['planoId']);
    } else if (currentStep === 4) {
      // passo 4 (Pagamento) apenas exige diaVencimento agora
      isValid = await trigger(['diaVencimento']);
    }
    
    if (isValid && currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      const enderecoSelecionado = enderecos.find(e => e.id == data.enderecoId);
      if (!enderecoSelecionado?.cidadeId) {
        toast.error('Endereço selecionado não possui cidade associada');
        setLoading(false);
        return;
      }
      
      const valorTotalVisual = calcularValorTotal();
      const valorMensalParaEnvio = parseFloat(planoSelecionado?.valor || 0);

      const payload = {
        clienteId: Number(data.clienteId),
        planoId: Number(data.planoId),
        enderecoId: Number(data.enderecoId),
        cidadeId: Number(enderecoSelecionado.cidadeId),
        dataInicio: format(new Date(), 'yyyy-MM-dd'),
        valorMensalidade: valorMensalParaEnvio,
        valorOriginal: parseFloat(planoSelecionado?.valor || 0),
        diaVencimento: Number(data.diaVencimento),
        status: 'ativo',
        formaPagamento: 'pix', // agora sempre PIX pelo front
        observacoes: data.observacoes || '',
        desconto: parseFloat(data.desconto) || 0
      };

      // mapear campos PPPoE do front para o que a API espera (login / senha)
      if (data.login_pppoe) payload.login = data.login_pppoe;
      if (data.senha_pppoe) payload.senha = data.senha_pppoe;
      
      let contratoId;
      
      if (isEdit) {
        await api.patch(`/contratos/${id}`, payload);
        contratoId = id;
        
        const servicosAtuais = await api.get(`/contratos/${id}/servicos-adicionais`);
        const servicosAtuaisIds = servicosAtuais.data.map(s => s.id);
        const servicosNovosIds = servicosSelecionados.map(s => s.id);
        
        for (const servicoId of servicosAtuaisIds) {
          if (!servicosNovosIds.includes(servicoId)) {
            await api.delete(`/contratos/${id}/servicos-adicionais`, {
              data: { servicoAdicionalId: servicoId }
            });
          }
        }
        
        for (const servicoId of servicosNovosIds) {
          if (!servicosAtuaisIds.includes(servicoId)) {
            await api.post(`/contratos/${id}/servicos-adicionais`, {
              servicoAdicionalId: servicoId
            });
          }
        }
        
        toast.success('Contrato atualizado com sucesso');
        navigate('/contratos');
      } else {
        const response = await api.post('/contratos', payload);
        contratoId = response.data.id;
        
        for (const servico of servicosSelecionados) {
          await api.post(`/contratos/${contratoId}/servicos-adicionais`, {
            servicoAdicionalId: servico.id
          });
        }
        
        toast.success('Contrato cadastrado com sucesso');
        
        setContratoIdCriado(contratoId);
        setShowEquipamentoModal(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar contrato');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const adicionarEquipamento = () => {
    if (!equipamentoData.equipamentoId) {
      toast.error('Selecione um equipamento');
      return;
    }

    const equipamento = equipamentosDisponiveis.find(e => e.id == equipamentoData.equipamentoId);
    if (!equipamento) {
      toast.error('Equipamento não encontrado');
      return;
    }

    const jaVinculado = equipamentosVinculados.find(e => e.equipamentoId === equipamento.id);
    if (jaVinculado) {
      toast.error('Equipamento já foi adicionado');
      return;
    }

    const novoEquipamento = {
      equipamentoId: equipamento.id,
      equipamento: equipamento,
      tipoVinculo: equipamentoData.tipoVinculo,
      valorMensal: parseFloat(equipamentoData.valorMensal) || 0,
      observacoes: equipamentoData.observacoes
    };

    setEquipamentosVinculados([...equipamentosVinculados, novoEquipamento]);
    
    setEquipamentoData({
      equipamentoId: '',
      tipoVinculo: 'comodato',
      valorMensal: 0,
      observacoes: ''
    });

    toast.success(`${equipamento.modelo} adicionado`);
  };

  const removerEquipamento = (equipamentoId) => {
    setEquipamentosVinculados(equipamentosVinculados.filter(e => e.equipamentoId !== equipamentoId));
    toast.success('Equipamento removido');
  };

  const finalizarVinculoEquipamentos = async () => {
    if (equipamentosVinculados.length === 0) {
      setShowEquipamentoModal(false);
      navigate('/contratos');
      return;
    }

    try {
      setLoadingEquipamento(true);
      
      for (const equip of equipamentosVinculados) {
        await api.post(`/contratos/${contratoIdCriado}/equipamentos`, {
          equipamentoId: Number(equip.equipamentoId),
          tipoVinculo: equip.tipoVinculo,
          valorMensal: parseFloat(equip.valorMensal || 0),
          observacoes: equip.observacoes || ''
        });
      }
      
      toast.success(`${equipamentosVinculados.length} equipamento(s) vinculado(s) com sucesso`);
      setShowEquipamentoModal(false);
      navigate('/contratos');
    } catch (error) {
      toast.error('Erro ao vincular equipamentos');
      console.error(error);
    } finally {
      setLoadingEquipamento(false);
    }
  };

  const pularVinculoEquipamento = () => {
    setShowEquipamentoModal(false);
    navigate('/contratos');
  };

  const [modoAdicaoEquipamento, setModoAdicaoEquipamento] = useState(false);

  const EquipamentoModal = () => {
    if (!showEquipamentoModal) return null;

    if (!modoAdicaoEquipamento && equipamentosVinculados.length === 0) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-cyan-100 dark:bg-cyan-900/30 rounded-full flex items-center justify-center">
                  <Router className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                Vincular Equipamento?
              </h2>
              
              <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                Deseja vincular algum equipamento a este contrato agora?
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  💡 Você pode pular esta etapa e vincular equipamentos depois através da página de detalhes do contrato.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={pularVinculoEquipamento}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium"
                >
                  Agora Não
                </button>
                <button
                  type="button"
                  onClick={() => setModoAdicaoEquipamento(true)}
                  className="flex-1 px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
                >
                  Sim, Vincular
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header do Modal */}
          <div className="sticky top-0 bg-white dark:bg-dark-card border-b border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Router className="w-6 h-6 text-cyan-500" />
                Vincular Equipamentos
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Adicione os equipamentos que deseja vincular ao contrato
              </p>
            </div>
            <button
              onClick={() => {
                setModoAdicaoEquipamento(false);
                setEquipamentosVinculados([]);
                setEquipamentoData({
                  equipamentoId: '',
                  tipoVinculo: 'comodato',
                  valorMensal: 0,
                  observacoes: ''
                });
                pularVinculoEquipamento();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              title="Fechar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Conteúdo do Modal */}
          <div className="p-6 space-y-6">
            {/* Equipamentos já vinculados */}
            {equipamentosVinculados.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Equipamentos a vincular ({equipamentosVinculados.length})
                </h3>
                <div className="space-y-3">
                  {equipamentosVinculados.map((equip, index) => (
                    <div key={index} className="flex items-start justify-between p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {equip.equipamento.modelo}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          MAC: {equip.equipamento.macAddress}
                          {equip.equipamento.tipo?.nome && ` - ${equip.equipamento.tipo.nome}`}
                        </p>
                        <div className="mt-2 flex items-center gap-4 text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            Tipo: <span className="font-medium capitalize text-gray-900 dark:text-white">{equip.tipoVinculo}</span>
                          </span>
                          <span className="text-cyan-600 dark:text-cyan-400 font-semibold">
                            R$ {parseFloat(equip.valorMensal).toFixed(2)}/mês
                          </span>
                        </div>
                        {equip.observacoes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Obs: {equip.observacoes}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removerEquipamento(equip.equipamentoId)}
                        className="ml-4 p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Remover equipamento"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Formulário de adição de equipamento */}
            <div className="space-y-6 p-6 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
              <h3 className="font-medium text-gray-900 dark:text-white">
                Adicionar Equipamento
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Equipamento *
                </label>
                <select
                  value={equipamentoData.equipamentoId}
                  onChange={(e) => setEquipamentoData({ ...equipamentoData, equipamentoId: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                >
                  <option value="">Selecione um equipamento...</option>
                  {equipamentosDisponiveis
                    .filter(equip => !equipamentosVinculados.find(v => v.equipamentoId === equip.id))
                    .map((equip) => (
                      <option key={equip.id} value={equip.id}>
                        {equip.modelo} - {equip.macAddress} {equip.tipo?.nome ? `(${equip.tipo.nome})` : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Vínculo *
                  </label>
                  <select
                    value={equipamentoData.tipoVinculo}
                    onChange={(e) => setEquipamentoData({ ...equipamentoData, tipoVinculo: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                  >
                    <option value="comodato">Comodato</option>
                    <option value="venda">Venda</option>
                    <option value="aluguel">Aluguel</option>
                    <option value="cortesia">Cortesia</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor Mensal (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={equipamentoData.valorMensal}
                    onChange={(e) => setEquipamentoData({ ...equipamentoData, valorMensal: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Deixe 0 para comodato gratuito
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observações
                </label>
                <textarea
                  value={equipamentoData.observacoes}
                  onChange={(e) => setEquipamentoData({ ...equipamentoData, observacoes: e.target.value })}
                  rows="3"
                  placeholder="Informações adicionais sobre o equipamento..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 transition-colors resize-none"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={adicionarEquipamento}
                  className="flex items-center gap-2 px-6 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
                >
                  <Plus className="w-5 h-5" />
                  Adicionar à Lista
                </button>
              </div>
            </div>

            {/* Info sobre a vinculação */}
            {equipamentosVinculados.length === 0 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800 dark:text-blue-400">Opcional</p>
                    <p className="text-sm text-blue-700 dark:text-blue-500 mt-1">
                      Você pode pular esta etapa e vincular equipamentos depois, através da página de detalhes do contrato.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer do Modal */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center">
            <button
              type="button"
              onClick={pularVinculoEquipamento}
              className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Pular esta etapa
            </button>

            <button
              type="button"
              onClick={finalizarVinculoEquipamentos}
              disabled={loadingEquipamento}
              className="px-8 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all"
            >
              <Check className="w-5 h-5" />
              {loadingEquipamento 
                ? 'Vinculando...' 
                : equipamentosVinculados.length > 0 
                  ? `Vincular ${equipamentosVinculados.length} Equipamento(s)` 
                  : 'Concluir sem Equipamentos'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderClienteStep();
      case 2:
        return renderPlanoStep();
      case 3:
        return renderServicosStep();
      case 4:
        return renderPagamentoStep();
      case 5:
        return renderConexaoStep();
      case 6:
        return renderResumoStep();
      default:
        return null;
    }
  };

  const renderClienteStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-blue-500">
        <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
          <User className="w-5 h-5 text-blue-500" />
          Selecione o Cliente e Endereço
        </h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cliente *
            </label>
            <select
              {...register('clienteId')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              disabled={isEdit}
            >
              <option value="">Selecione o cliente...</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome} - {cliente.cpfCnpj}
                </option>
              ))}
            </select>
            {errors.clienteId && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.clienteId.message}
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Endereço de Instalação *
            </label>
            <select
              {...register('enderecoId')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              disabled={!clienteId || loadingEnderecos}
            >
              <option value="">
                {!clienteId 
                  ? 'Selecione um cliente primeiro...' 
                  : loadingEnderecos 
                  ? 'Carregando endereços...' 
                  : enderecos.length === 0 
                  ? 'Cliente sem endereços cadastrados' 
                  : 'Selecione o endereço...'}
              </option>
              {enderecos.map(endereco => (
                <option key={endereco.id} value={endereco.id}>
                  {endereco.logradouro}, {endereco.numero} - {endereco.bairro}
                </option>
              ))}
            </select>
            {errors.enderecoId && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.enderecoId.message}
              </p>
            )}
            {enderecoId && enderecos.find(e => e.id == enderecoId) && (
              <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Cidade será automaticamente associada ao endereço selecionado
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlanoStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-green-500">
        <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
          <Wifi className="w-5 h-5 text-green-500" />
          Escolha o Plano
        </h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Plano *
            </label>
            <select
              {...register('planoId')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-800 transition-colors"
            >
              <option value="">Selecione o plano...</option>
              {planos.filter(p => p.disponivelComercial === 'S').map(plano => (
                <option key={plano.id} value={plano.id}>
                  {plano.nomePlano} - {plano.limitDownload}/{plano.limitUpload} MB - R$ {plano.valor}
                </option>
              ))}
            </select>
            {errors.planoId && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.planoId.message}
              </p>
            )}
          </div>

          {planoSelecionado && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-lg p-6 border-2 border-green-200 dark:border-gray-600">
              <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white">
                {planoSelecionado.nomePlano}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Download</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{planoSelecionado.limitDownload} MB</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Upload</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{planoSelecionado.limitUpload} MB</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Tecnologia</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{planoSelecionado.tipoTecnologia}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Valor</p>
                  <p className="font-bold text-green-600 dark:text-green-400 text-lg">R$ {planoSelecionado.valor}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderServicosStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-purple-500">
        <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
          <Package className="w-5 h-5 text-purple-500" />
          Serviços Adicionais (Opcional)
        </h2>

        {servicosSelecionados.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Serviços Selecionados ({servicosSelecionados.length})
            </h3>
            <div className="space-y-2">
              {servicosSelecionados.map(servico => (
                <div key={servico.id} className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{servico.nome}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{servico.descricao}</p>
                    <p className="text-sm font-semibold text-green-600 dark:text-green-400 mt-1">
                      + R$ {parseFloat(servico.valorMensal).toFixed(2)}/mês
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removerServicoAdicional(servico.id)}
                    className="ml-4 p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Serviços Disponíveis
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {servicosAdicionais
              .filter(s => !servicosSelecionados.find(sel => sel.id === s.id))
              .map(servico => (
                <div key={servico.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-500 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">{servico.nome}</h4>
                    {servico.categoria && (
                      <span className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                        {servico.categoria}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{servico.descricao}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        R$ {parseFloat(servico.valorMensal).toFixed(2)}
                        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mês</span>
                      </p>
                      {servico.valorInstalacao > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Instalação: R$ {parseFloat(servico.valorInstalacao).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => adicionarServicoAdicional(servico)}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar
                    </button>
                  </div>
                </div>
              ))}
          </div>
          {servicosAdicionais.filter(s => !servicosSelecionados.find(sel => sel.id === s.id)).length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              Todos os serviços disponíveis já foram adicionados
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderPagamentoStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-orange-500">
        <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
          <CreditCard className="w-5 h-5 text-orange-500" />
          Dados de Pagamento
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dia de Vencimento *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                min="1"
                max="31"
                {...register('diaVencimento')}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-800 transition-colors"
                placeholder="Ex: 10"
              />
            </div>
            {errors.diaVencimento && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.diaVencimento.message}
              </p>
            )}
          </div>
          
          {/* Forma de pagamento removida do formulário (será padrão PIX) */}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observações
            </label>
            <textarea
              {...register('observacoes')}
              rows="4"
              placeholder="Informações adicionais sobre o contrato..."
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:bg-gray-800 transition-colors resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderConexaoStep = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-cyan-500">
        <h2 className="text-lg font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-white">
          <Router className="w-5 h-5 text-cyan-500" />
          Dados de Conexão (PPPoE)
        </h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Login PPPoE</label>
            <input
              type="text"
              {...register('login_pppoe')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              placeholder="login do provedor"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Senha PPPoE</label>
            <input
              type="password"
              {...register('senha_pppoe')}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 transition-colors"
              placeholder="senha do provedor"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderResumoStep = () => {
    const valorPlano = planoSelecionado ? parseFloat(planoSelecionado.valor) : 0;
    const valorServicos = servicosSelecionados.reduce((total, s) => total + parseFloat(s.valorMensal), 0);
    const valorTotal = valorPlano + valorServicos;
    const clienteSelecionado = clientes.find(c => c.id == clienteId);
    const enderecoSelecionado = enderecos.find(e => e.id == enderecoId);

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-blue-500">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <User className="w-5 h-5 text-blue-500" />
            Cliente
          </h3>
          {clienteSelecionado && (
            <div className="space-y-2">
              <p className="text-gray-900 dark:text-white"><span className="font-medium">Nome:</span> {clienteSelecionado.nome}</p>
              <p className="text-gray-900 dark:text-white"><span className="font-medium">CPF/CNPJ:</span> {clienteSelecionado.cpfCnpj}</p>
              {enderecoSelecionado && (
                <p className="text-gray-900 dark:text-white">
                  <span className="font-medium">Endereço:</span> {enderecoSelecionado.logradouro}, {enderecoSelecionado.numero} - {enderecoSelecionado.bairro}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-green-500">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <Wifi className="w-5 h-5 text-green-500" />
            Plano Contratado
          </h3>
          {planoSelecionado && (
            <div className="space-y-2">
              <p className="text-gray-900 dark:text-white"><span className="font-medium">Plano:</span> {planoSelecionado.nomePlano}</p>
              <p className="text-gray-900 dark:text-white">
                <span className="font-medium">Velocidade:</span> {planoSelecionado.limitDownload}/{planoSelecionado.limitUpload} MB
              </p>
              <p className="text-gray-900 dark:text-white">
                <span className="font-medium">Valor:</span> <span className="text-green-600 dark:text-green-400 font-bold">R$ {valorPlano.toFixed(2)}</span>
              </p>
            </div>
          )}
        </div>

        {servicosSelecionados.length > 0 && (
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-purple-500">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Package className="w-5 h-5 text-purple-500" />
              Serviços Adicionais ({servicosSelecionados.length})
            </h3>
            <div className="space-y-3">
              {servicosSelecionados.map(servico => (
                <div key={servico.id} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{servico.nome}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{servico.descricao}</p>
                  </div>
                  <p className="font-semibold text-purple-600 dark:text-purple-400">
                    R$ {parseFloat(servico.valorMensal).toFixed(2)}
                  </p>
                </div>
              ))}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <p className="font-medium text-gray-900 dark:text-white">Subtotal Serviços:</p>
                  <p className="font-bold text-purple-600 dark:text-purple-400">
                    R$ {valorServicos.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-orange-500">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <CreditCard className="w-5 h-5 text-orange-500" />
            Pagamento
          </h3>
          <div className="space-y-2">
            <p className="text-gray-900 dark:text-white">
              <span className="font-medium">Vencimento:</span> Dia {watch('diaVencimento')} de cada mês
            </p>
            <p className="text-gray-900 dark:text-white">
              <span className="font-medium">Forma de Pagamento:</span> {
                // sempre PIX no front
                'PIX'
              }
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6 border-l-4 border-l-cyan-500">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <Router className="w-5 h-5 text-cyan-500" />
            Conexão (PPPoE)
          </h3>
          <div className="space-y-2">
            <p className="text-gray-900 dark:text-white">
              <span className="font-medium">Login:</span> {watch('login_pppoe') || '-'}
            </p>
            <p className="text-gray-900 dark:text-white">
              <span className="font-medium">Senha:</span> {watch('senha_pppoe') ? '••••••••' : '-'}
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6">
          <div className="text-center text-white">
            <p className="text-sm mb-2">Valor Total da Mensalidade</p>
            <p className="text-5xl font-bold mb-2">R$ {valorTotal.toFixed(2)}</p>
            <div className="text-sm opacity-90">
              <p>Plano: R$ {valorPlano.toFixed(2)}</p>
              {valorServicos > 0 && <p>Serviços Adicionais: R$ {valorServicos.toFixed(2)}</p>}
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-400">Revise as informações</p>
              <p className="text-sm text-yellow-700 dark:text-yellow-500 mt-1">
                Certifique-se de que todos os dados estão corretos antes de finalizar o contrato.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/contratos')}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEdit ? 'Editar Contrato' : 'Novo Contrato'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {isEdit ? 'Atualize as informações do contrato' : 'Preencha os dados para criar um novo contrato'}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all
                  ${currentStep > step.id 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : currentStep === step.id 
                    ? 'bg-blue-500 border-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                  }
                `}>
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <div className="text-center mt-2">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.id 
                      ? 'text-gray-900 dark:text-white' 
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">
                    {step.description}
                  </p>
                </div>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`
                  flex-1 h-0.5 mx-2 transition-all
                  ${currentStep > step.id 
                    ? 'bg-green-500' 
                    : 'bg-gray-300 dark:bg-gray-700'
                  }
                `} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} id="contrato-form">
        {renderStepContent()}
      </form>

      <div className="flex justify-between items-center sticky bottom-0 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={previousStep}
          disabled={currentStep === 1}
          className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-medium"
        >
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </button>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/contratos')}
            className="px-6 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all flex items-center gap-2 font-medium"
          >
            <X className="w-4 h-4" />
            Cancelar
          </button>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                nextStep(e);
              }}
              className="px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Próximo
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="submit"
              form="contrato-form"
              disabled={loading}
              className="px-8 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Salvando...' : isEdit ? 'Atualizar Contrato' : 'Finalizar Contrato'}
            </button>
          )}
        </div>
      </div>

      <EquipamentoModal />
    </div>
  );
};

export default ContratoForm;